// src/hooks/useVendorTracking.js
import { useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';

// ✅ PHASE 3: Derive socket URL from backend URL, no hardcoded fallback to api.honestlee.ae
const getSocketUrl = () => {
  // Priority 1: Explicit socket URL
  if (process.env.REACT_APP_SOCKET_URL) {
    return process.env.REACT_APP_SOCKET_URL;
  }
  
  // Priority 2: Derive from backend URL
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Priority 3: Local dev - use current origin (preview/localhost)
  // This works because backend and frontend are on same origin in dev (via proxy)
  return window.location.origin;
};

const SOCKET_URL = getSocketUrl();

console.log('🔌 Socket.IO URL from .env:', SOCKET_URL);

export const useVendorTracking = (vendorIds = [], onLocationUpdate, onStatusChange) => {
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  useEffect(() => {
    if (!vendorIds || vendorIds.length === 0) return;

    console.log('🔌 Connecting to Socket.IO at:', SOCKET_URL);
    console.log('📍 Vendor IDs to track:', vendorIds);

    socketRef.current = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
      withCredentials: true,
      secure: SOCKET_URL.includes('https'),
      rejectUnauthorized: false
    });

    const socket = socketRef.current;

    socket.on('connect', () => {
      console.log('✅ Socket.IO CONNECTED:', socket.id);
      console.log('✅ Connected to:', SOCKET_URL);
      
      vendorIds.forEach(vendorId => {
        socket.emit('track-vendor', vendorId);
        console.log(`📍 Tracking vendor: ${vendorId}`);
      });
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Socket.IO CONNECTION FAILED');
      console.error('   Error:', error.message);
      console.error('   URL:', SOCKET_URL);
      console.error('   Make sure:');
      console.error('   1. Backend is running on port 4000');
      console.error('   2. Socket.IO is enabled in app.ts');
      console.error('   3. CORS is configured correctly');
    });

    socket.on('disconnect', (reason) => {
      console.log('👋 Socket disconnected:', reason);
    });

    socket.on('vendor-location-update', (data) => {
      console.log('📡 Location update received:', data);
      if (onLocationUpdate) {
        onLocationUpdate(data);
      }
    });

    socket.on('vendor-status-change', (data) => {
      console.log('🔄 Status change received:', data);
      if (onStatusChange) {
        onStatusChange(data);
      }
    });

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      vendorIds.forEach(vendorId => {
        socket.emit('untrack-vendor', vendorId);
      });
      
      socket.disconnect();
      console.log('🧹 Socket cleaned up');
    };
  }, [vendorIds.join(','), onLocationUpdate, onStatusChange]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
  }, []);

  return { socket: socketRef.current, disconnect };
};
